import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { normalizeUserAttributes, User } from '../../../store/user/user.models';
import { selectAllUsers, selectUser } from '../../../store/user/user.selectors';
import { UserGetAllRequest, UserManageDeleteRequest, UserManageStatusRequest } from '../../../store/user/user.actions';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { environment } from '../../../../environments/environment';
import { OrganizationGetRequest, OrganizationUpdateRequest } from '../../../store/organization/organization.actions';
import { selectOrganization, selectOrganizationValidationErrors } from '../../../store/organization/organization.selectors';
import type { MeetingProviderConfig, Organization } from '../../../store/organization/organization.models';
import { BugReportFetchRequest } from '../../../store/bug-report/bug-report.actions';
import { selectBugReports, selectBugReportsLoading } from '../../../store/bug-report/bug-report.selectors';
import type { BugReport } from '../../../store/bug-report/bug-report.models';
import { ValidationErrorComponent } from '../../../components/core/validation-error/validation-error.component';
import { AddUserDialogComponent } from './components/add-user-dialog/add-user-dialog.component';
import { ConfirmComponent } from '../../../components/core/confirm/confirm.component';
import { take } from 'rxjs';
import { BRANDING } from '../../../core/config/branding';

type AdminTab = 'users' | 'bugs' | 'company';

type ManagedUser = {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  status: string;
  isActive: boolean;
  isOwner: boolean;
  isSelf: boolean;
};

type ManagedBugReport = {
  id: string;
  title: string;
  description: string;
  pageUrl: string;
  appVersion: string;
  screenshotUrl: string;
  reporterName: string;
  reporterEmail: string;
  createdAt: string;
};

@Component({
  selector: 'app-admin-management',
  imports: [
    RouterLink,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    ValidationErrorComponent
  ],
  templateUrl: './admin-management.html',
  styleUrl: './admin-management.scss',
})
export class AdminManagement {
  protected readonly brand = BRANDING;
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly formBuilder = inject(FormBuilder);
  private readonly dialog = inject(MatDialog);
  protected readonly activeTab = signal<AdminTab>('users');
  protected userInfo = signal<User | null>(null);
  protected organization = signal<Organization | null>(null);
  private readonly organizationId = signal<string | null>(null);
  protected readonly organizationUsers = signal<ManagedUser[]>([]);
  protected readonly organizationUsersById = signal<Record<string, User>>({});
  protected readonly bugReports = signal<BugReport[]>([]);
  protected readonly bugReportsLoading = signal(false);
  private readonly bugReportsRequested = signal(false);
  protected readonly searchTerm = signal('');
  protected readonly pageSize = signal(5);
  protected readonly currentPage = signal(1);
  protected readonly bugSearchTerm = signal('');
  protected readonly bugPageSize = signal(5);
  protected readonly bugCurrentPage = signal(1);
  protected readonly pageSizeOptions = [5, 10, 20];
  protected readonly validationError = signal<string[]>([]);
  protected readonly companyForm = this.formBuilder.group({
    name: ['', [Validators.required, Validators.minLength(2)]],
    email: ['', [Validators.required, Validators.email]],
    phone: ['', [Validators.required]],
    address_line_1: ['', [Validators.required]],
    address_line_2: [''],
    city: ['', [Validators.required]],
    state: ['', [Validators.required]],
    country: ['', [Validators.required]],
    zip: ['', [Validators.required]],
    daily_api_key: [''],
    daily_base_url: [''],
    daily_domain: [''],
    daily_disabled: [false],
  });

  constructor() {
    this.store.dispatch(UserGetAllRequest());

    this.store.select(selectUser)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        this.userInfo.set(user);
        if (this.activeTab() === 'company' && !this.canManageCompany()) {
          this.activeTab.set('users');
        }
        if (this.activeTab() === 'bugs' && !this.canManageBugReports()) {
          this.activeTab.set('users');
        }
        const orgId = user?.organization_id ?? null;
        if (orgId && orgId !== this.organizationId()) {
          this.organizationId.set(orgId);
          this.store.dispatch(OrganizationGetRequest({ id: orgId }));
        }

        if (this.canManageBugReports() && !this.bugReportsRequested()) {
          this.bugReportsRequested.set(true);
          this.store.dispatch(BugReportFetchRequest());
        }
      });

    this.store.select(selectAllUsers)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((users) => {
        const list = users ?? [];
        const mappedUsers = list.map((user) => this.mapManagedUser(user));
        this.organizationUsers.set(mappedUsers);
        this.organizationUsersById.set(
          list.reduce<Record<string, User>>((accumulator, user) => {
            if (user?.id) {
              accumulator[user.id] = user;
            }
            return accumulator;
          }, {})
        );
        this.currentPage.set(1);
      });

    this.store.select(selectBugReports)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((reports) => {
        this.bugReports.set(reports ?? []);
        this.bugCurrentPage.set(1);
      });

    this.store.select(selectBugReportsLoading)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((loading) => this.bugReportsLoading.set(!!loading));

    this.store.select(selectOrganization)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((organization) => {
        this.organization.set(organization);
        if (organization) {
          this.patchOrganizationForm(organization);
        }
      });

    this.store.select(selectOrganizationValidationErrors)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((errors) => {
        if (Array.isArray(errors)) {
          this.validationError.set(errors as string[]);
        }
      });
  }

  protected setTab(tab: AdminTab): void {
    if (tab === 'company' && !this.canManageCompany()) {
      return;
    }
    if (tab === 'bugs' && !this.canManageBugReports()) {
      return;
    }
    this.activeTab.set(tab);
  }

  protected updateSearchTerm(value: string): void {
    this.searchTerm.set(value);
    this.currentPage.set(1);
  }

  protected updatePageSize(value: string): void {
    const size = Number(value);
    if (!Number.isFinite(size) || size <= 0) {
      return;
    }

    this.pageSize.set(size);
    this.currentPage.set(1);
  }

  protected updateBugSearchTerm(value: string): void {
    this.bugSearchTerm.set(value);
    this.bugCurrentPage.set(1);
  }

  protected updateBugPageSize(value: string): void {
    const size = Number(value);
    if (!Number.isFinite(size) || size <= 0) {
      return;
    }

    this.bugPageSize.set(size);
    this.bugCurrentPage.set(1);
  }

  protected previousPage(): void {
    this.goToPage(this.currentPage() - 1);
  }

  protected nextPage(): void {
    this.goToPage(this.currentPage() + 1);
  }

  protected goToPage(page: number): void {
    const bounded = Math.min(Math.max(page, 1), this.totalPages());
    this.currentPage.set(bounded);
  }

  protected previousBugPage(): void {
    this.goToBugPage(this.bugCurrentPage() - 1);
  }

  protected nextBugPage(): void {
    this.goToBugPage(this.bugCurrentPage() + 1);
  }

  protected goToBugPage(page: number): void {
    const bounded = Math.min(Math.max(page, 1), this.totalBugPages());
    this.bugCurrentPage.set(bounded);
  }

  protected pagedUsers(): ManagedUser[] {
    const filtered = this.filteredUsers();
    const start = (this.currentPage() - 1) * this.pageSize();
    return filtered.slice(start, start + this.pageSize());
  }

  protected filteredUsersCount(): number {
    return this.filteredUsers().length;
  }

  protected totalPages(): number {
    const total = this.filteredUsersCount();
    return Math.max(1, Math.ceil(total / this.pageSize()));
  }

  protected pagedBugReports(): ManagedBugReport[] {
    const filtered = this.filteredBugReports();
    const start = (this.bugCurrentPage() - 1) * this.bugPageSize();
    return filtered.slice(start, start + this.bugPageSize());
  }

  protected filteredBugReportsCount(): number {
    return this.filteredBugReports().length;
  }

  protected totalBugPages(): number {
    const total = this.filteredBugReportsCount();
    return Math.max(1, Math.ceil(total / this.bugPageSize()));
  }

  protected isPreviousDisabled(): boolean {
    return this.currentPage() <= 1;
  }

  protected isNextDisabled(): boolean {
    return this.currentPage() >= this.totalPages();
  }

  protected isPreviousBugDisabled(): boolean {
    return this.bugCurrentPage() <= 1;
  }

  protected isNextBugDisabled(): boolean {
    return this.bugCurrentPage() >= this.totalBugPages();
  }

  protected visibleRangeStart(): number {
    const total = this.filteredUsersCount();
    if (total === 0) {
      return 0;
    }

    return (this.currentPage() - 1) * this.pageSize() + 1;
  }

  protected visibleRangeEnd(): number {
    return Math.min(this.currentPage() * this.pageSize(), this.filteredUsersCount());
  }

  protected visibleBugRangeStart(): number {
    const total = this.filteredBugReportsCount();
    if (total === 0) {
      return 0;
    }

    return (this.bugCurrentPage() - 1) * this.bugPageSize() + 1;
  }

  protected visibleBugRangeEnd(): number {
    return Math.min(this.bugCurrentPage() * this.bugPageSize(), this.filteredBugReportsCount());
  }

  private filteredUsers(): ManagedUser[] {
    const query = this.searchTerm().trim().toLowerCase();
    if (!query) {
      return this.organizationUsers();
    }

    return this.organizationUsers().filter((user) => {
      return (
        user.name.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query) ||
        user.phone.toLowerCase().includes(query)
      );
    });
  }

  private filteredBugReports(): ManagedBugReport[] {
    const query = this.bugSearchTerm().trim().toLowerCase();
    const reports = this.bugReports().map((report) => this.mapManagedBugReport(report));

    if (!query) {
      return reports;
    }

    return reports.filter((report) => {
      return (
        report.title.toLowerCase().includes(query) ||
        report.description.toLowerCase().includes(query) ||
        report.pageUrl.toLowerCase().includes(query) ||
        report.reporterName.toLowerCase().includes(query) ||
        report.reporterEmail.toLowerCase().includes(query) ||
        report.appVersion.toLowerCase().includes(query)
      );
    });
  }

  private mapManagedUser(user: User): ManagedUser {
    const normalizedAttributes = normalizeUserAttributes(user.attributes);
    const normalizedRole = typeof user.role === 'string' && user.role.trim()
      ? user.role
      : typeof normalizedAttributes['role'] === 'string'
        ? normalizedAttributes['role']
        : '';
    const currentUserId = this.userInfo()?.id ?? '';
    const isOwner = normalizedAttributes['is_owner'] === true
      || normalizedAttributes['isOwner'] === true
      || normalizedRole.trim().toLowerCase() === 'owner';

    return {
      id: user.id,
      name: user.name || 'Unknown User',
      email: user.email || '-',
      phone: user.phone || '-',
      role: this.formatRole(normalizedRole, normalizedAttributes),
      status: this.resolveStatus(user),
      isActive: !!user.is_active,
      isOwner,
      isSelf: !!currentUserId && user.id === currentUserId,
    };
  }

  private mapManagedBugReport(report: BugReport): ManagedBugReport {
    const reporter = this.organizationUsersById()[report.user_id];

    return {
      id: report.id,
      title: report.title || 'Untitled bug report',
      description: report.description || '-',
      pageUrl: this.toRenderableAssetUrl(report.page_url) ?? '-',
      appVersion: report.app_version || '-',
      screenshotUrl: this.toRenderableAssetUrl(report.screenshot_url) ?? '',
      reporterName: reporter?.name || 'Unknown user',
      reporterEmail: reporter?.email || '-',
      createdAt: this.formatBugReportDate(report.created_at),
    };
  }

  private formatBugReportDate(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '-';
    }

    return date.toLocaleString([], {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  private toRenderableAssetUrl(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    if (/^(https?:|blob:|data:)/i.test(trimmed)) {
      return trimmed;
    }

    if (trimmed.startsWith('/')) {
      return new URL(trimmed, environment.apiBaseUrl).toString();
    }

    return null;
  }

  private formatRole(role: string, attributes: Record<string, unknown>): string {
    if (attributes['is_owner'] === true || attributes['isOwner'] === true) {
      return 'Owner';
    }

    if (attributes['is_admin'] === true || attributes['isAdmin'] === true) {
      return 'Admin';
    }

    const normalizedRole = role.trim().toLowerCase();
    if (!normalizedRole) {
      return 'Member';
    }

    return normalizedRole.charAt(0).toUpperCase() + normalizedRole.slice(1);
  }

  private resolveStatus(user: User): string {
    if (!user.is_active) {
      return 'Suspended';
    }

    if (!user.is_verified) {
      return 'Invited';
    }

    return 'Active';
  }

  protected canManageUsers(): boolean {
    const role = this.getUserRole();
    return role === 'admin' || role === 'owner' || role === 'superadmin';
  }

  protected canManageBugReports(): boolean {
    const role = this.getUserRole();
    return role === 'superadmin';
  }

  protected openAddUserDialog(): void {
    if (!this.canManageUsers()) {
      return;
    }

    this.dialog.open(AddUserDialogComponent, {
      width: '520px',
      maxWidth: 'calc(100vw - 24px)',
      autoFocus: false,
    });
  }

  protected openEditUserDialog(user: ManagedUser): void {
    const sourceUser = this.organizationUsersById()[user.id];
    if (!sourceUser || !this.canManageUsers()) {
      return;
    }

    this.dialog.open(AddUserDialogComponent, {
      width: '520px',
      maxWidth: 'calc(100vw - 24px)',
      autoFocus: false,
      data: {
        mode: 'edit',
        user: sourceUser,
      },
    });
  }

  protected canToggleUserStatus(user: ManagedUser): boolean {
    return this.canManageUsers() && !user.isOwner && !user.isSelf;
  }

  protected canDeleteUser(user: ManagedUser): boolean {
    return this.canManageUsers() && !user.isOwner && !user.isSelf;
  }

  protected toggleUserStatus(user: ManagedUser): void {
    if (!this.canToggleUserStatus(user)) {
      return;
    }

    const nextActiveState = !user.isActive;
    this.dialog.open(ConfirmComponent, {
      width: '420px',
      maxWidth: 'calc(100vw - 24px)',
      data: {
        title: nextActiveState ? 'Activate User' : 'Deactivate User',
        message: nextActiveState
          ? `Activate ${user.name}? They will be able to access ${this.brand.name} again.`
          : `Deactivate ${user.name}? They will no longer be able to access ${this.brand.name}.`,
      },
    }).afterClosed().pipe(take(1)).subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }

      this.store.dispatch(UserManageStatusRequest({
        userId: user.id,
        isActive: nextActiveState,
      }));
    });
  }

  protected deleteUser(user: ManagedUser): void {
    if (!this.canDeleteUser(user)) {
      return;
    }

    this.dialog.open(ConfirmComponent, {
      width: '420px',
      maxWidth: 'calc(100vw - 24px)',
      data: {
        title: 'Delete User',
        message: `Delete ${user.name}? This action cannot be undone.`,
      },
    }).afterClosed().pipe(take(1)).subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }

      this.store.dispatch(UserManageDeleteRequest({ userId: user.id }));
    });
  }

  protected canManageCompany(): boolean {
    const role = this.getUserRole();
    return role === 'owner' || role === 'superadmin';
  }

  private getUserRole(): string {
    const user = this.userInfo();
    const userRole = typeof user?.role === 'string' ? user.role.toLowerCase() : '';
    if (userRole) {
      return userRole;
    }

    const attrs = user?.attributes;
    if (!attrs) {
      return '';
    }

    const normalizedAttributes = normalizeUserAttributes(attrs);

    if (normalizedAttributes['is_owner'] === true || normalizedAttributes['isOwner'] === true) {
      return 'owner';
    }
    if (normalizedAttributes['is_admin'] === true || normalizedAttributes['isAdmin'] === true) {
      return 'admin';
    }

    return typeof normalizedAttributes['role'] === 'string' ? normalizedAttributes['role'].toLowerCase() : '';
  }

  protected onCancelCompany(): void {
    this.validationError.set([]);
    const org = this.organization();
    if (org) {
      this.patchOrganizationForm(org);
    }
  }

  protected onSaveCompany(): void {
    const org = this.organization();
    if (!org) {
      return;
    }

    this.validationError.set([]);
    if (this.companyForm.invalid) {
      this.companyForm.markAllAsTouched();
      return;
    }

    const formValue = this.companyForm.getRawValue();
    const existingConfig = org.config ?? {};
    const existingMeeting = existingConfig.meeting ?? {};
    const providers = { ...(existingMeeting.providers ?? {}) };
    const existingDaily = providers['daily'];

    const dailyConfig: MeetingProviderConfig = {
      ...(existingDaily ?? {}),
      type: 'daily',
      api_key: (formValue.daily_api_key ?? '').trim() || undefined,
      base_url: (formValue.daily_base_url ?? '').trim() || undefined,
      domain: (formValue.daily_domain ?? '').trim() || undefined,
      disabled: !!formValue.daily_disabled,
    };

    providers['daily'] = dailyConfig;

    const defaultProvider = (existingMeeting.default_provider ?? '').trim();
    const resolvedDefault = defaultProvider || (providers['daily'] ? 'daily' : '');

    this.store.dispatch(OrganizationUpdateRequest({
      payload: {
        id: org.id,
        organization: {
          name: (formValue.name ?? '').trim(),
          email: (formValue.email ?? '').trim(),
          phone: (formValue.phone ?? '').trim(),
          address: {
            address_line_1: (formValue.address_line_1 ?? '').trim(),
            address_line_2: (formValue.address_line_2 ?? '').trim(),
            city: (formValue.city ?? '').trim(),
            state: (formValue.state ?? '').trim(),
            country: (formValue.country ?? '').trim(),
            zip: (formValue.zip ?? '').trim(),
          },
          config: {
            ...existingConfig,
            meeting: {
              ...existingMeeting,
              default_provider: resolvedDefault,
              providers,
            },
          },
          membership: org.membership ?? { is_member: false },
          is_active: org.is_active ?? true,
        }
      }
    }));
  }

  private patchOrganizationForm(organization: Organization): void {
    const dailyConfig = organization.config?.meeting?.providers?.['daily'];
    this.companyForm.reset({
      name: organization.name ?? '',
      email: organization.email ?? '',
      phone: organization.phone ?? '',
      address_line_1: organization.address?.address_line_1 ?? '',
      address_line_2: organization.address?.address_line_2 ?? '',
      city: organization.address?.city ?? '',
      state: organization.address?.state ?? '',
      country: organization.address?.country ?? '',
      zip: organization.address?.zip ?? '',
      daily_api_key: dailyConfig?.api_key ?? '',
      daily_base_url: dailyConfig?.base_url ?? '',
      daily_domain: dailyConfig?.domain ?? '',
      daily_disabled: dailyConfig?.disabled ?? false,
    });
  }
}
